import {
  actualizarPregunta,
  crearPregunta,
  eliminarPregunta,
  getPreguntas,
} from 'api/pregunta'

import { 
  getPreguntaGrupoInteresByfield,
  eliminarPreguntaGrupoInteres,
  crearPreguntaGrupoInteres, 
} from 'api/preguntaGrupoInteres'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import swal from 'sweetalert2'

const key = 'preguntas'
export function useObtenerPregunta(buscar) {
  return useQuery(key, () => getPreguntas(buscar))
}

export function useCrearPregunta() {
  const queryClient = useQueryClient()
  return useMutation(crearPregunta, {
    onSuccess: (response) => {
      const { data } = response
      queryClient.setQueryData(key, (prevState) => {
        const results = [data, ...prevState.results]
        return { ...prevState, results }
      })
    },
  })
}

export function useActualizarPregunta() {
  const queryClient = useQueryClient()
  return useMutation(actualizarPregunta, {
    onSuccess: (_, value) => {
      const { id, values: preguntaActualizada } = value
      queryClient.setQueryData(key, (prevState) => {
        const results = prevState.results.map((pregunta) => {
          let nuevaPregunta = pregunta
          if (id === pregunta.id) {
            nuevaPregunta = preguntaActualizada
          }
          return nuevaPregunta
        })
        return { ...prevState, results }
      })
    },
  })
}

export function useEliminarPregunta() {
  const queryClient = useQueryClient()
  return useMutation(eliminarPregunta, {
    onSuccess: (_, value) => {
      queryClient.setQueryData(key, (prevState) => {
        const results = prevState.results.filter(
          (pregunta) => pregunta.id !== value.id,
        )
        return { ...prevState, results }
      })
    },
  })
}


const obtenerPreguntaGrupoInteres = async (id) => {
  const response = await getPreguntaGrupoInteresByfield('preguntas__id', id)
  return response.results
}

const relacionarPreguntasYGruposInteres = async (grupos_interes, preguntaId) => {
  if (grupos_interes.length > 0) {
    await Promise.all(grupos_interes.map((preguntaGrupoInteres) =>
      crearPreguntaGrupoInteres({ grupo_interes: preguntaGrupoInteres, preguntas: preguntaId })
    ))
  }
}

const desrelacionarPreguntasYGruposInteres = async (id) => {
  const relacionPreguntas = await obtenerPreguntaGrupoInteres(id)
  if (relacionPreguntas.length > 0) {
    await Promise.all(relacionPreguntas.map((preguntaGrupoInteres) =>
      eliminarPreguntaGrupoInteres(preguntaGrupoInteres)
    ))
  }
}

export function usePregunta(onToggle) {
  const queryClient = useQueryClient()
  const { mutateAsync: eliminarPregunta } = useEliminarPregunta()
  const { mutateAsync: actualizarPregunta } = useActualizarPregunta()
  const { mutateAsync: crearPregunta } = useCrearPregunta()


  const handleCrearPregunta = async ({ grupos_interes, ...values}, acciones) => {
    try {
      const { data, onSuccessMessage } = await crearPregunta(values)
      await relacionarPreguntasYGruposInteres(grupos_interes, data.id)

      const message = `La pregunta ${data.nombre} fue creada satisfactoriamente!`
      onSuccessMessage({ text: message })
      acciones.resetForm()
      onToggle()
    } catch (error) {
      acciones.setErrors(error)
    }
  }

  const handleActualizarPregunta = async ({ 
    id,
    values: {
      grupos_interes,
      ...values
    },
    acciones
  }) => {
    try {
      await desrelacionarPreguntasYGruposInteres(id)
      await relacionarPreguntasYGruposInteres(grupos_interes, id)
      const { data, onSuccessMessage } = await actualizarPregunta({
        id,
        values,
      })
      if (data) {
        onSuccessMessage({
          text: 'La pregunta fue actualizada satisfactoriamente!',
        })
        onToggle()
      }
    } catch (error) {
      acciones.setErrors(error)
    }
  }

  const handleEliminarPregunta = (pregunta) => {
    swal
      .fire({
        title: 'Estás seguro?',
        text: 'Recuerda que no podrás revertir estos cambios!',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si, eliminala!',
      })
      .then(async ({ value }) => {
        if (value) {
          const { data, onSuccessMessage } = await eliminarPregunta(pregunta)
          const message = `La pregunta ${data} fue eliminada satisfactoriamente!`
          onSuccessMessage({ text: message })
        }
      })
  }

  const handleBuscarPregunta = async (value) => {
    queryClient.prefetchQuery(key, () => getPreguntas(value))
  }

  return {
    obtenerPreguntas: useObtenerPregunta,
    handleEliminarPregunta,
    handleActualizarPregunta,
    handleCrearPregunta,
    handleBuscarPregunta,
  }
}
